import { getActivityIcon, getActivityColor, getActivityLabel } from '@/components/activity/activityTypes'

// Color manipulation utilities with dark mode support
export const getLighterColor = (color, opacity = 0.8, isDarkMode = false) => {
  if (typeof color === 'string' && !color.startsWith('#')) {
    // Handle Vuetify theme colors if needed
    color = color // This could be enhanced to resolve theme colors
  }

  let r = parseInt(color.slice(1, 3), 16)
  let g = parseInt(color.slice(3, 5), 16)
  let b = parseInt(color.slice(5, 7), 16)

  if (isDarkMode) {
    // For dark mode, create a more vibrant, lighter version
    r = Math.round(r + (255 - r) * 0.3)
    g = Math.round(g + (255 - g) * 0.3)
    b = Math.round(b + (255 - b) * 0.3)
    return `rgba(${r}, ${g}, ${b}, 0.15)`
  } else {
    // For light mode, use the original logic with the passed opacity
    r = Math.round(r + (255 - r) * opacity)
    g = Math.round(g + (255 - g) * opacity)
    b = Math.round(b + (255 - b) * opacity)
    return `rgba(${r}, ${g}, ${b}, 0.5)`
  }
}

export const getDarkerColor = (color, amount = 0.2, isDarkMode = false) => {
  if (typeof color === 'string' && !color.startsWith('#')) {
    // Handle Vuetify theme colors if needed
    color = color
  }

  const hex = color.replace('#', '')
  let r = parseInt(hex.substr(0, 2), 16)
  let g = parseInt(hex.substr(2, 2), 16) 
  let b = parseInt(hex.substr(4, 2), 16)

  if (isDarkMode) {
    // For dark mode, create a brighter, more vibrant version
    r = Math.round(r + (255 - r) * 0.2)
    g = Math.round(g + (255 - g) * 0.2)
    b = Math.round(b + (255 - b) * 0.2)
  } else {
    // For light mode, use the original darkening logic
    r = Math.floor(r * (1 - amount))
    g = Math.floor(g * (1 - amount))
    b = Math.floor(b * (1 - amount))
  }

  return (
    '#' +
    (r < 16 ? '0' : '') +
    r.toString(16) +
    (g < 16 ? '0' : '') +
    g.toString(16) +
    (b < 16 ? '0' : '') +
    b.toString(16)
  )
}

// Enhanced color utilities for better dark mode support
export const getTimelineColor = (type, isDarkMode = false) => {
  const baseColor = getActivityColor(type)
  
  // Always return the same base color for consistency
  // The color manipulation functions (getLighterColor, getDarkerColor) will handle
  // the brightness adjustments for dark mode
  return baseColor
}

export const getTimelineBackgroundColor = (type, isDarkMode = false) => {
  const baseColor = getActivityColor(type)
  
  if (isDarkMode) {
    // For dark mode, create subtle background colors
    const darkModeBackgrounds = {
      'financial': 'rgba(76, 175, 80, 0.1)',    // Subtle green
      'status': 'rgba(33, 150, 243, 0.1)',      // Subtle blue
      'inventory': 'rgba(255, 152, 0, 0.1)',    // Subtle orange
      'logistics': 'rgba(0, 188, 212, 0.1)',    // Subtle cyan
      'administrative': 'rgba(156, 39, 176, 0.1)', // Subtle purple
      'order': 'rgba(63, 81, 181, 0.1)',        // Subtle indigo
      'task': 'rgba(0, 188, 212, 0.1)',         // Subtle teal
      'checklist': 'rgba(76, 175, 80, 0.1)',    // Subtle green
      'comment': 'rgba(255, 152, 0, 0.1)',      // Subtle orange
      'note': 'rgba(255, 152, 0, 0.1)',         // Subtle orange
      'attachment': 'rgba(156, 39, 176, 0.1)',  // Subtle purple
      'scheduled': 'rgba(33, 150, 243, 0.1)',   // Subtle blue
      'email': 'rgba(21, 101, 192, 0.1)',       // Subtle blue
      'call': 'rgba(233, 30, 99, 0.1)',         // Subtle pink
      'account': 'rgba(96, 125, 139, 0.1)',     // Subtle blue grey
      'brand': 'rgba(121, 85, 72, 0.1)',        // Subtle brown
      'contact': 'rgba(158, 158, 158, 0.1)',    // Subtle grey
      'documentation': 'rgba(96, 125, 139, 0.1)', // Subtle blue grey
      'system': 'rgba(96, 125, 139, 0.1)'       // Subtle blue grey
    }
    return darkModeBackgrounds[type] || `rgba(117, 117, 117, 0.1)`
  }
  
  // For light mode, use the original lighter color logic
  return getLighterColor(baseColor, 0.8)
}

// File utilities
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export const getFileIcon = (fileType) => {
  if (fileType.startsWith('image/')) return 'mdi-file-image'
  if (fileType === 'application/pdf') return 'mdi-file-pdf'
  if (fileType.includes('word')) return 'mdi-file-word'
  if (fileType.includes('excel')) return 'mdi-file-excel'
  return 'mdi-file'
}

// User utilities
export const getInitials = (fullName) => {
  return fullName
    .split(' ')
    .map((name) => name[0])
    .join('')
    .toUpperCase()
}

// Activity type utilities (wrapping the imported functions)
export const getTimelineIcon = (type) => getActivityIcon(type)
export const getTimelineLabel = (type) => getActivityLabel(type)

// Mention utilities
export const checkForMentions = (note, users = []) => {
  if (!note || typeof note !== 'string') return []

  const mentionRegex = /@([\w\s]+?)(?:\s*\|\s*([\w\s]+))?(?=\s|$|[.,!?])/gi
  const mentions = new Set()

  let match
  while ((match = mentionRegex.exec(note)) !== null) {
    const rawName = match[1].trim().toLowerCase()
    const rawDept = match[2] ? match[2].trim().toLowerCase() : null

    const mentionedUser = users.find((user) => {
      // Handle different user structures
      let firstName = ''
      let lastName = ''
      let fullName = ''
      let department = null

      if (user.name && user.name.first && user.name.last) {
        // Structure: { name: { first: 'John', last: 'Doe' } }
        firstName = user.name.first.toLowerCase()
        lastName = user.name.last.toLowerCase()
        fullName = `${firstName} ${lastName}`
      } else if (user.text) {
        // Structure: { text: 'John Doe', fullName: 'John Doe' }
        fullName = user.text.toLowerCase()
        const nameParts = user.text.split(' ')
        firstName = nameParts[0] || ''
        lastName = nameParts.slice(1).join(' ') || ''
      }

      if (user.department) {
        department = user.department.toLowerCase()
      }

      const nameMatches =
        rawName === firstName ||
        rawName === lastName ||
        rawName === fullName ||
        fullName.includes(rawName)

      if (rawDept) {
        return nameMatches && department && department.includes(rawDept)
      }

      return nameMatches
    })

    if (mentionedUser) {
      mentions.add(mentionedUser)
    }
  }

  return Array.from(mentions)
} 